import React, { useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { PortalUsersGrid } from "../grids/PortalUsersGrid";
import { InvitePortalUserDialog } from "../dialogs/InvitePortalUserDialog";
import { EditPortalUserDialog } from "../dialogs/EditPortalUserDialog";
import { RemovePortalUserConfirmModal } from "../dialogs/RemovePortalUserConfirmModal";
import {
  useGetPortalUsersQuery,
  useResendPortalUserInviteMutation,
} from "../../../../services/ProfileService";
import type { PortalUserView } from "../../../../services/ProfileService/types/portalUserTypes";

interface PortalUsersTabProps {
  practiceId: string;
  practiceName: string;
  company?: string;
}

export function PortalUsersTab({ practiceId, practiceName, company = "" }: PortalUsersTabProps) {
  const { data: users = [], isLoading, error } = useGetPortalUsersQuery({ practiceId });
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editUser, setEditUser] = useState<PortalUserView | null>(null);
  const [removeUser, setRemoveUser] = useState<PortalUserView | null>(null);
  const [resendConfirm, setResendConfirm] = useState<PortalUserView | null>(null);
  const [snackbar, setSnackbar] = useState<{ message: string; severity: "success" | "error" } | null>(null);

  const [resendInvite, { isLoading: isResending }] = useResendPortalUserInviteMutation();

  const handleMutationSuccess = (message: string) => {
    setSnackbar({ message, severity: "success" });
  };

  const handleAction = (action: string, user: PortalUserView) => {
    switch (action) {
      case "edit":
        setEditUser(user);
        break;
      case "remove":
        setRemoveUser(user);
        break;
      case "resend":
        setResendConfirm(user);
        break;
    }
  };

  const handleResendConfirm = async () => {
    if (!resendConfirm) return;
    try {
      await resendInvite({ oid: resendConfirm.oid, practiceId }).unwrap();
      setSnackbar({ message: `Invitation resent to ${resendConfirm.mail}.`, severity: "success" });
    } catch (err: any) {
      if (err?.status === 409) {
        setSnackbar({ message: "User has already accepted the invitation.", severity: "error" });
      } else {
        setSnackbar({ message: err?.data?.message || "Failed to resend invitation.", severity: "error" });
      }
    }
    setResendConfirm(null);
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load portal users. Please try again.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Portal Users
        </Typography>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => setInviteOpen(true)}
        >
          Invite User
        </Button>
      </Stack>

      {/* Counts */}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Total: {users.length}
        </Typography>
        <Typography variant="body2" color="success.main">
          Active: {users.filter(u => u.status === "Active").length}
        </Typography>
        <Typography variant="body2" color="warning.main">
          Pending: {users.filter(u => u.status === "Pending").length}
        </Typography>
        <Typography variant="body2" color="error.main">
          Disabled: {users.filter(u => u.status === "Disabled").length}
        </Typography>
      </Stack>

      {/* Grid */}
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <PortalUsersGrid
          users={users}
          isLoading={isLoading}
          onRowSelect={() => {}}
          onAction={handleAction}
        />
      )}

      {/* Invite Dialog */}
      <InvitePortalUserDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSuccess={handleMutationSuccess}
        practiceId={practiceId}
      />

      {/* Edit Dialog */}
      <EditPortalUserDialog
        open={!!editUser}
        onClose={() => setEditUser(null)}
        user={editUser}
        company={company}
      />

      {/* Remove/Disable Confirm Modal */}
      <RemovePortalUserConfirmModal
        open={!!removeUser}
        onClose={() => setRemoveUser(null)}
        user={removeUser}
        onSuccess={handleMutationSuccess}
      />

      {/* Resend Confirm Dialog */}
      <Dialog open={!!resendConfirm} onClose={() => setResendConfirm(null)}>
        <DialogTitle>Resend Invitation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Resend the invitation email to {resendConfirm?.mail}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResendConfirm(null)} disabled={isResending}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleResendConfirm}
            disabled={isResending}
            startIcon={isResending ? <CircularProgress size={16} /> : undefined}
          >
            {isResending ? "Sending..." : "Resend"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snackbar ? (
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)}>
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
